import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import * as turf from '@turf/turf'

interface Layer {
  id: string
  enabled: boolean
  visible?: boolean
}

interface UseDistanceLimitLayerProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  geoJson?: any
  distanceLimitValue?: number
  layers: Layer[]
  styleLoadedCount: number
}

const SOURCE_ID = 'distance-limit-source'
const LAYER_ID = 'distance-limit-layer'

/**
 * Hook to render an inward buffer contour on the parcel polygon
 * representing the construction distance limit.
 */
export const useDistanceLimitLayer = ({
  map,
  mapLoaded,
  geoJson,
  distanceLimitValue,
  layers,
  styleLoadedCount,
}: UseDistanceLimitLayerProps) => {
  const prevValueRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const layerConfig = layers.find((l) => l.id === 'distance-limit')
    const isVisible = layerConfig?.visible ?? layerConfig?.enabled ?? true

    const hasValidData = geoJson && distanceLimitValue && distanceLimitValue > 0

    // Hide layer when toggled off but keep source for quick re-show
    if (!isVisible && map.current.getLayer(LAYER_ID)) {
      map.current.setLayoutProperty(LAYER_ID, 'visibility', 'none')
      return
    }

    if (!hasValidData) {
      try {
        if (map.current.getLayer(LAYER_ID)) {
          map.current.removeLayer(LAYER_ID)
        }
        if (map.current.getSource(SOURCE_ID)) {
          map.current.removeSource(SOURCE_ID)
        }
      } catch {
        // ignore cleanup errors
      }
      prevValueRef.current = undefined
      return
    }

    try {
      // Normalize geoJson to a Feature or FeatureCollection
      const normalized =
        geoJson.type === 'Feature' || geoJson.type === 'FeatureCollection'
          ? geoJson
          : { type: 'Feature', geometry: geoJson, properties: {} }

      // Compute the inward buffer (negative distance)
      const buffered = turf.buffer(normalized, -distanceLimitValue, {
        units: 'meters',
      })

      if (!buffered) {
        if (map.current.getLayer(LAYER_ID)) {
          map.current.removeLayer(LAYER_ID)
        }
        if (map.current.getSource(SOURCE_ID)) {
          map.current.removeSource(SOURCE_ID)
        }
        return
      }

      // Update existing source or create source + layer
      const existingSource = map.current.getSource(SOURCE_ID) as
        | mapboxgl.GeoJSONSource
        | undefined

      if (existingSource) {
        existingSource.setData(buffered)
        if (map.current.getLayer(LAYER_ID)) {
          map.current.setLayoutProperty(LAYER_ID, 'visibility', 'visible')
        }
      } else {
        map.current.addSource(SOURCE_ID, {
          type: 'geojson',
          data: buffered,
        })

        const firstSymbolLayer = map.current
          .getStyle()
          ?.layers?.find((l) => l.type === 'symbol')

        map.current.addLayer(
          {
            id: LAYER_ID,
            type: 'line',
            source: SOURCE_ID,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#FF6B35',
              'line-width': 2,
              'line-opacity': 0.85,
              'line-dasharray': [4, 3],
            },
          },
          firstSymbolLayer?.id
        )
      }

      prevValueRef.current = distanceLimitValue
    } catch (error) {
      console.warn('Failed to render distance limit buffer:', error)
    }
  }, [geoJson, distanceLimitValue, layers, mapLoaded, styleLoadedCount])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map.current) return
      try {
        if (map.current.getLayer(LAYER_ID)) {
          map.current.removeLayer(LAYER_ID)
        }
        if (map.current.getSource(SOURCE_ID)) {
          map.current.removeSource(SOURCE_ID)
        }
      } catch {
        // map may already be removed
      }
    }
  }, [])
}
